import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";
import { IOem } from "@/Settings/types/SystemSettings/appendingConfiguration";

const queriesMap: Record<string, string> = {
	MaxPTO: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				maxAllowableBorrowedPTO
			}
		}
	`,
	CIT_EMAIL_DISTRIBUTION: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				citEmailDistribution {
					toIndividuals {
						_id
						employee {
							_id
							employeeId
							fullName
							jobTitle
						}
					}
					toNonEmployee
					accountNumbers {
						sourceAccount
						destinationAccount
						sourceLabelId
					}
				}
			}
		}
	`,
	CROSS_STORE_EMAIL_DISTRIBUTION: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				crossStoreEmailDistribution {
					toIndividuals {
						_id
						employeeId
					}
					toNonEmployee
					accountMappings {
						sourceGlAccount
						destinationStore {
							storeId
							storeName
						}
						destinationGlAccount
						destinationSourceLabel {
							label
							value
						}
						destinationControlType {
							label
							value
							identifier
						}
					}
				}
			}
		}
	`,
	salesLog: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				salesLogs {
					franchises
					exportFormat
					dailyDistributions
					toPositions
					toIndividuals {
						_id
						employee {
							communications {
								value
								usage
								communicationType
								acceptsTxt
							}
							employeeId
							fullName
							_id
						}
					}
					toEmails
				}
			}
		}
	`,
	SALES_LOG_INITIAL_DATA_FILTER: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				salesLogInitialDataFilter {
					name
					filterOptions {
						name
						included
						enabled
						disabled
					}
				}
			}
		}
	`,
	"Technical Contacts": gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				technicalContacts
			}
		}
	`,
	"Commission Support Team": gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				commissionSupportTeam {
					_id
					fullName
					position
					employeeId
					profilePicture
					communications {
						communicationType
						usage
						value
					}
				}
			}
		}
	`,
	"Washout Reviewers": gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				washoutReviewers {
					lastModifiedBy {
						_id
						employeeId
						communications {
							acceptsTxt
							communicationType
							usage
							value
						}
						fullName
						position
					}
					lastModifiedOn
					position
					reviewerId {
						_id
						communications {
							acceptsTxt
							communicationType
							usage
							value
						}
						employeeId
						fullName
						position
					}
					isDirectSupervisor
				}
			}
		}
	`,
	"Commission Cycles Per Month": gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				commissionCyclesPerMonth {
					lastModifiedOn
					value
					lastModifiedBy {
						_id
						communications {
							acceptsTxt
							communicationType
							usage
							value
						}
						employeeId
						fullName
						position
					}
				}
			}
		}
	`,
	"Chargeback Limits": gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				chargebackLimits {
					lastModifiedOn
					position
					value
					lastModifiedBy {
						_id
						communications {
							acceptsTxt
							usage
							value
							communicationType
						}
						employeeId
						fullName
						position
					}
				}
			}
		}
	`,
	FI_RANKING_TYPE: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				isGlobalRanking
			}
		}
	`,
	MINIMUM_WAGE: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				minimumWage
			}
		}
	`,
	CHARGEBACK_TYPES: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				chargebackTypes {
					position
					grossChargebackTypes {
						chargebackCode
						productType
					}
					deductionsChargebackTypes {
						chargebackCode
						earningPayItem
						deductionPayItem
					}
				}
			}
		}
	`,
	CIT_MAX_UNFUNDED_DOLLARS: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				citMaxGraphValues {
					maxUnfundedDollars
					maxUnfundedContracts
				}
			}
		}
	`,
	CIT_MAX_UNFUNDED_CONTRACTS: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				citMaxGraphValues {
					maxUnfundedDollars
					maxUnfundedContracts
				}
			}
		}
	`,
	CIT_GRAPH_WIDTH: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				citGraphWidth
			}
		}
	`,
	CIT_COMMISSION_PAYOUT: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				citCommissionPayout {
					isPayFundedDealsOnly
					toPositions
				}
			}
		}
	`,
	MAX_PVR_VALUES: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				pvrMaxGraphValues {
					maxNewVehiclesPVR
					maxPreOwnedVehiclesPVR
					maxFinancePVR
					maxTotalPVR
				}
			}
		}
	`,
	CALL_ACTIVITY_PERFORMANCE: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				callActivityPerformance {
					maxAllowableTurnaroundTime {
						hours
						minutes
					}
					minimumTalkDuration {
						minutes
						seconds
					}
					minimumRingDuration {
						seconds
					}
					minimumTotalDuration {
						minutes
						seconds
					}
					enableTalkTime
					enableRingTime
					enableTotalTime
				}
			}
		}
	`,
	SCHEDULED_ACCOUNT_NUMBERS: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				citScheduledAccountNumbers {
					label
					values
				}
			}
		}
	`,
	MINIMUM_BALANCE: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				citMinimumBalance
			}
		}
	`,
	MAX_SCHEDULE_DAYS: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				citMaxScheduleDays
			}
		}
	`,
	BLACK_LIST_PHONE_NUMBERS: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				blacklistPhoneNumbers {
					_id
					phoneNumber
					createdAt
				}
			}
		}
	`,
	WORKING_DAYS: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				workingDays {
					workingDays
					position
				}
			}
		}
	`,
	DRAW_MODALITIES: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				drawModalities {
					drawModality
					position
				}
			}
		}
	`,
	TERMS_AND_CONDITIONS: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				payplanTermConditions {
					position
					inEditionBy {
						_id
						fullName
					}
					termConditions {
						_id
						componentName
						sectionTitle
						content
						createdAt
						createdBy
						updatedAt
						updatedBy
					}
				}
			}
		}
	`,
	DEFAULT_PAY_PLANS: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				defaultPayPlans {
					position
					defaultPayPlan
					newVehiclePayPlan
					preOwnedVehiclePayPlan
				}
			}
		}
	`,
	MAPPED_POSITIONS: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				mappedPositions {
					systemPosition
					overtime
				}
			}
		}
	`,
	APPENDING_CONFIGURATION: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput, $oems: [OemInput]) {
			updateSystemSettings(locationId: $locationId, input: $input, oems: $oems) {
				_id
				locationId
				appendingConfiguration {
					_id
					position
					data {
						_id
						reference
						oems {
							_id
							configuration {
								_id
								franchise
								isCombined
								groups {
									franchiseName
								}
								data {
									_id
									accountDescription
									fsAccount
									glAccount {
										glAccount
										accountType
									}
									groupInfo {
										group
										subGroup
									}
									arithmeticExpression {
										operation
										value
									}
									properties {
										isChargeback
									}
								}
							}
							oemReference
							dealerCodeFS
							fsVersion
							version
						}
					}
				}
			}
		}
	`,
	CHARGEBACK_ACCOUNT_REFERENCES: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				chargebackAccountReferences {
					position
					chargebackTypes {
						label
						description
						repairOrderRoute
						accountNumbers
					}
				}
			}
		}
	`,
	ADVISORS_RANKING_CONFIGURATION: gql`
		mutation updateSystemSettings($locationId: String!, $input: SystemSettingsInput) {
			updateSystemSettings(locationId: $locationId, input: $input) {
				_id
				locationId
				advisorsRankingConfiguration {
					isRankingRO
					category
				}
			}
		}
	`,
};

export async function updateSystemSettings(locationId: string, input: unknown, component: string, oems?: IOem[]) {
	const query = queriesMap[component];

	const variables: {
		locationId: string;
		input: unknown;
		oems?: IOem[];
	} = {
		locationId,
		input,
	};

	if (oems) {
		variables.oems = oems;
	}

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateSystemSettings;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export const updatePdfAvailability = async (locationId: string, position: string, pdfAvailability: boolean, isDepartmental = false) => {
	const query = gql`
		mutation updatePdfAvailability($locationId: String!, $position: String!, $pdfAvailability: Boolean!, $isDepartmental: Boolean!) {
			updatePdfAvailability(
				position: $position
				locationId: $locationId
				pdfAvailability: $pdfAvailability
				isDepartmental: $isDepartmental
			) {
				_id
				locationId
				position
				pdfAvailability
			}
		}
	`;

	const variables = {
		locationId,
		position,
		pdfAvailability,
		isDepartmental,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response?.data?.data?.updatePdfAvailability;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
};
